import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import { formatSize, getFloorString } from '@/features/rapidstor/helpers';
import { descriptorDimensions } from '@/core/utils/rapidstor';

// Hook to centralise size category derivation so behaviour stays consistent.
export function useSizeCategoryKey(
    unitType: UnitTypeWithImages | undefined,
    location: Location | undefined,
) {
    return useMemo(() => {
        if (!unitType || !location) return 'na';
        const size = unitType.width * unitType.length;
        if (size <= location.rapidstor.smallMediumSizes) return 'small';
        if (
            size > location.rapidstor.smallMediumSizes
            && size < location.rapidstor.mediumLargeSizes
        ) return 'medium';
        return 'large';
    }, [unitType, location]);
}

// Narrow interface for expected classNames we rely on. Extra keys are allowed via index signature.
// We keep this broad so passing a CSS module object (index signature only) type-checks.
export interface UnitTypeMetaClasses {
    [key: string]: string;
}

export interface UnitTypeMetaProps {
    unitType: UnitTypeWithImages;
    location: Location;
    classes: UnitTypeMetaClasses;
    hideLevel?: boolean;
}

function UnitTypeMeta({
    unitType,
    location,
    classes,
    hideLevel,
}: UnitTypeMetaProps) {
    const { t } = useTranslation();

    return (
        <div className={classes.features}>
            <div className={classes.block}>
                <span className={classes.label}>
                    {location.rapidstor.areaSizeSymbol}
                </span>
                <span className={classes.value}>
                    {formatSize(unitType.width * unitType.length, location, true, true)}
                </span>
            </div>
            <div className={classes.block}>
                <span className={classes.label}>
                    {t('standardUnitListing:unitType:size')}
                </span>
                <span
                    className={classes.value}
                >
                    {descriptorDimensions(unitType, location, { supplementary: true })}
                </span>
            </div>
            <div className={classes.block}>
                <span className={classes.label}>
                    {t('standardUnitListing:unitType:type')}
                </span>
                <span className={classes.value}>{unitType.typeName}</span>
            </div>
            {!hideLevel && (
                <div className={classes.block}>
                    <span className={classes.label}>
                        {t('standardUnitListing:unitType:level')}
                    </span>
                    <span className={classes.value}>
                        {getFloorString(unitType, location)}
                    </span>
                </div>
            )}
        </div>
    );
}

export default UnitTypeMeta;
